import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionGetCCPaperDetail } from '../../store/Actions/PatientAction';
import { Card, Tag, Empty, Skeleton, Tooltip } from 'antd';
import {
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    CalendarOutlined,
    IdcardOutlined,
    ManOutlined,
    WomanOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import './PatientCCPaperView.css';

// Helper function to get field descriptions for tooltips
const getFieldDescription = (fieldName) => {
    const descriptions = {
        'Phone': 'Primary contact number',
        'Email': 'Email address for communication',
        'Gender': 'Patient gender',
        'Birth Date': 'Date of birth',
        'Age': 'Current age of the patient',
        'Registration No': 'Unique hospital registration number',
        'Old Case No': 'Previous case paper number',
        'Height': 'Patient height in cm',
        'Weight': 'Patient weight in kg',
        'Address': 'Residential address',
        'Membership': 'Active membership status',
        'Membership Type': 'Type of membership plan',
        'Remaining Days': 'Days remaining in membership',
        'Total Treatments': 'Total treatments received',
        'Total Appointments': 'Total appointments booked',
        'Consultations': 'Total consultations completed',
        'Total Booking Days': 'Total days of bookings',
        'Emergency Number': 'Emergency contact number',
        'Vehicle No': 'Vehicle registration number',
        'Arriving From': 'Arrival date',
        'Arrival Time': 'Expected arrival time',
        'Aadhar Cards': 'Aadhar card numbers',
        'Insurance Company': 'Insurance provider name',
        'Policy Number': 'Insurance policy number',
        'Policy Holder': 'Name of policy holder',
        'Claim Amount': 'Insurance claim amount',
        'Insurance Contact': 'Insurance company contact',
    };
    return descriptions[fieldName] || `Information about ${fieldName}`;
};

// Reusable InfoCard component with hover, tooltip, and large text handling
const InfoCard = ({ icon, label, value, tooltip }) => {
    const [copied, setCopied] = React.useState(false);

    // Check if value is long (more than 30 characters)
    const isLongText = value && value.toString().length > 30;

    // Truncate text for display
    const displayValue = isLongText
        ? `${value.toString().substring(0, 30)}...`
        : value;

    // Handle click to copy
    const handleClick = () => {
        if (value) {
            navigator.clipboard.writeText(value.toString()).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }).catch(err => {
                console.error('Failed to copy:', err);
            });
        }
    };

    // Enhanced tooltip content for long text
    const tooltipContent = isLongText ? (
        <div>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>{tooltip}</div>
            <div style={{
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                maxWidth: '300px',
                wordWrap: 'break-word'
            }}>
                {value}
            </div>
            <div style={{
                marginTop: '8px',
                fontSize: '11px',
                opacity: 0.8,
                fontStyle: 'italic'
            }}>
                Click to copy
            </div>
        </div>
    ) : (
        <div>
            <div>{tooltip}</div>
            {value && value.toString().length > 0 && (
                <div style={{
                    marginTop: '4px',
                    fontSize: '11px',
                    opacity: 0.8,
                    fontStyle: 'italic'
                }}>
                    Click to copy
                </div>
            )}
        </div>
    );

    return (
        <Tooltip title={tooltipContent} placement="top">
            <div
                style={{
                    padding: '12px 16px',
                    background: copied ? '#f0f9ff' : '#fafafa',
                    border: copied ? '1px solid #3b82f6' : '1px solid #e8e8e8',
                    borderRadius: '6px',
                    cursor: isLongText ? 'pointer' : 'help',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                }}
                onClick={handleClick}
                onMouseEnter={(e) => {
                    if (!copied) {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                        e.currentTarget.style.borderColor = '#d0d0d0';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (!copied) {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e8e8e8';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }
                }}
            >
                {copied && (
                    <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        fontSize: '10px',
                        color: '#3b82f6',
                        fontWeight: '600',
                        background: '#fff',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                    }}>
                        ✓ Copied
                    </div>
                )}
                <div style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    marginBottom: '6px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px'
                }}>
                    {icon} {label}
                    <InfoCircleOutlined style={{
                        fontSize: '11px',
                        opacity: 0.5,
                        marginLeft: 'auto'
                    }} />
                </div>
                <div style={{
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#1f2937',
                    letterSpacing: '-0.2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {displayValue}
                </div>
            </div>
        </Tooltip>
    );
};

// Group Header component for section titles with collapse functionality
const GroupHeader = ({ title, isCollapsed, onToggle }) => {
    return (
        <div
            style={{
                gridColumn: '1 / -1',
                marginTop: '16px',
                marginBottom: isCollapsed ? '8px' : '8px',
                paddingBottom: '8px',
                borderBottom: '2px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
            onClick={onToggle}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
            }}
        >
            <h4 style={{
                margin: 0,
                fontSize: '13px',
                fontWeight: '700',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                userSelect: 'none',
            }}>
                <span style={{
                    width: '4px',
                    height: '16px',
                    background: '#6b7280',
                    borderRadius: '2px',
                }}></span>
                {title}
                <span style={{
                    marginLeft: 'auto',
                    fontSize: '14px',
                    transition: 'transform 0.2s ease',
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                }}>
                    ▼
                </span>
            </h4>
        </div>
    );
};

/**
 * PatientCCPaperView Component
 * Clean, minimal card-based layout for patient information
 * This component handles its own data fetching with proper caching to prevent duplicate API calls
 * Supports both flat extraInfo and grouped groupedInfo for better organization
 */
export const PatientCCPaperView = ({ userId, onDataFetch, extraInfo = {}, groupedInfo = {} }) => {
    const dispatch = useDispatch();
    const { CcPaperDetail, loader } = useSelector(state => state?.patient);

    // Track which userId we've already fetched to prevent duplicate calls
    const fetchedUserIdRef = useRef(null);
    const isFetchingRef = useRef(false);

    // State for managing collapsed groups
    const [collapsedGroups, setCollapsedGroups] = React.useState({});

    // Toggle collapse state for a group
    const toggleGroup = (groupTitle) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupTitle]: !prev[groupTitle]
        }));
    };

    // Memoized function to check if we have correct user data
    const isCorrectUser = useCallback(() => {
        if (!CcPaperDetail || !userId) return false;
        // Handle both string IDs and cases where _id might be the full userId
        const currentId = CcPaperDetail?._id?.toString() || CcPaperDetail?._id;
        const targetId = userId?.toString() || userId;
        return currentId === targetId;
    }, [CcPaperDetail, userId]);

    useEffect(() => {
        // Prevent fetch if:
        // 1. No userId provided
        // 2. Already fetching
        // 3. Already fetched for this userId
        // 4. Redux loader is active
        // 5. Already have correct data for this user
        if (!userId) {
            return;
        }

        const hasCorrectData = isCorrectUser();
        const alreadyFetchedThisUser = fetchedUserIdRef.current === userId;

        if (hasCorrectData || alreadyFetchedThisUser || isFetchingRef.current || loader) {
            if (hasCorrectData) {
                console.log('PatientCCPaperView: Using cached patient data for userId:', userId);
            }
            return;
        }

        // Mark as fetching and store the userId we're fetching
        isFetchingRef.current = true;
        fetchedUserIdRef.current = userId;
        dispatch(ActionGetCCPaperDetail({ Query: `/${userId}` }));

        // Reset fetching flag after a short delay to allow the request to complete
        const timer = setTimeout(() => {
            isFetchingRef.current = false;
        }, 1000);

        return () => clearTimeout(timer);
    }, [userId, dispatch, loader, isCorrectUser]); // Added isCorrectUser as dependency

    useEffect(() => {
        if (CcPaperDetail && CcPaperDetail._id === userId && typeof onDataFetch === 'function') {
            onDataFetch(CcPaperDetail);
        }
    }, [CcPaperDetail, userId]);

    if (loader && (!CcPaperDetail || CcPaperDetail._id !== userId)) {
        return (
            <Card
                style={{
                    marginBottom: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                bodyStyle={{ padding: '20px' }}
            >
                {/* Header Skeleton */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                    padding: '20px',
                    background: 'linear-gradient(135deg, #f5f7fc 0%, #e5e8f5 100%)',
                    borderRadius: '8px',
                    border: '1px solid #d8dce9'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <Skeleton.Avatar active size={60} />
                        <div>
                            <Skeleton.Input active style={{ width: 180, marginBottom: 8 }} />
                            <Skeleton.Input active size="small" style={{ width: 120 }} />
                        </div>
                    </div>
                    <Skeleton.Avatar active size={48} />
                </div>
                {/* Info Grid Skeleton */}
                <div className="row g-3">
                    {[1, 2, 3, 4].map((item) => (
                        <div key={item} className="col-md-6 col-lg-3">
                            <div style={{
                                padding: '12px 16px',
                                background: '#fafafa',
                                border: '1px solid #e8e8e8',
                                borderRadius: '6px'
                            }}>
                                <Skeleton.Input active size="small" style={{ width: 60, marginBottom: 6 }} />
                                <Skeleton.Input active style={{ width: '100%' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    if (!CcPaperDetail || Object.keys(CcPaperDetail).length === 0) {
        return (
            <div className="p-3">
                <Empty description="No patient details found" />
            </div>
        );
    }

    const genderIcon = CcPaperDetail.gender === 'Female' ? <WomanOutlined /> : <ManOutlined />;

    return (
        <Card
            style={{
                marginBottom: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}
            bodyStyle={{ padding: '20px' }}
        >
            {/* Header Row - Enhanced & Eye-Catching */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                padding: '20px',
                background: 'linear-gradient(135deg, #f5f7fc 0%, #e5e8f5 100%)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(46, 55, 164, 0.08)',
                border: '1px solid #d8dce9'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(46, 55, 164, 0.12)'
                    }}>
                        <UserOutlined style={{ fontSize: '28px', color: '#2e37a4' }} />
                    </div>
                    <div>
                        <h2 style={{
                            margin: 0,
                            fontSize: '26px',
                            fontWeight: '600',
                            color: '#1a202c',
                            letterSpacing: '-0.5px',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                        }}>
                            {CcPaperDetail.name}
                        </h2>
                        <Tag
                            style={{
                                marginTop: '8px',
                                fontSize: '12px',
                                fontWeight: '500',
                                padding: '4px 12px',
                                borderRadius: '4px',
                                background: 'rgba(46, 55, 164, 0.1)',
                                color: '#2e37a4',
                                border: '1px solid rgba(46, 55, 164, 0.2)',
                                letterSpacing: '0.3px'
                            }}
                        >
                            <IdcardOutlined /> UHID: {CcPaperDetail.registrationNo || 'NEW'}
                        </Tag>
                    </div>
                </div>
                <div style={{
                    fontSize: '48px',
                    color: '#2e37a4'
                }}>
                    {genderIcon}
                </div>
            </div>

            {/* Info Grid */}
            <div className="row g-3">
                {/* Phone */}
                <div className="col-md-6 col-lg-3">
                    <InfoCard
                        icon={<PhoneOutlined />}
                        label="Phone"
                        value={("+" + CcPaperDetail?.countryCode) + " " + CcPaperDetail.phoneNo || '---'}
                        tooltip={getFieldDescription('Phone')}
                    />
                </div>

                {/* Email */}
                <div className="col-md-6 col-lg-3">
                    <InfoCard
                        icon={<MailOutlined />}
                        label="Email"
                        value={CcPaperDetail.email || '---'}
                        tooltip={getFieldDescription('Email')}
                    />
                </div>

                {/* Gender */}
                <div className="col-md-6 col-lg-3">
                    <InfoCard
                        icon={genderIcon}
                        label="Gender"
                        value={CcPaperDetail.gender || '---'}
                        tooltip={getFieldDescription('Gender')}
                    />
                </div>

                {/* Birth Date */}
                <div className="col-md-6 col-lg-3">
                    <InfoCard
                        icon={<CalendarOutlined />}
                        label="Birth Date"
                        value={CcPaperDetail.birthDate
                            ? new Date(CcPaperDetail.birthDate).toLocaleDateString('en-GB')
                            : '---'}
                        tooltip={getFieldDescription('Birth Date')}
                    />
                </div>


                {/* Grouped Info or Extra Info */}
                {Object.keys(groupedInfo).length > 0 ? (
                    // Render grouped information with headers
                    Object.entries(groupedInfo).map(([groupTitle, groupFields]) => {
                        const filteredFields = Object.entries(groupFields).filter(
                            ([_, value]) => value !== undefined && value !== null && value !== '' && value !== '---'
                        );

                        if (filteredFields.length === 0) return null;

                        const isCollapsed = collapsedGroups[groupTitle];

                        return (
                            <React.Fragment key={groupTitle}>
                                <GroupHeader
                                    title={groupTitle}
                                    isCollapsed={isCollapsed}
                                    onToggle={() => toggleGroup(groupTitle)}
                                />
                                {!isCollapsed && filteredFields.map(([key, value]) => (
                                    <div
                                        key={key}
                                        className="col-md-6 col-lg-3"
                                        style={{
                                            animation: 'fadeIn 0.3s ease-in',
                                        }}
                                    >
                                        <InfoCard
                                            label={key}
                                            value={value}
                                            tooltip={getFieldDescription(key)}
                                        />
                                    </div>
                                ))}
                            </React.Fragment>
                        );
                    })
                ) : (
                    // Fallback to flat extraInfo for backward compatibility
                    Object.entries(extraInfo).filter(([_, value]) => value !== undefined && value !== null && value !== '' && value !== '---').map(([key, value]) => (
                        <div key={key} className="col-md-6 col-lg-3">
                            <InfoCard
                                label={key}
                                value={value}
                                tooltip={getFieldDescription(key)}
                            />
                        </div>
                    ))
                )}
            </div>
        </Card>
    );
};

export default PatientCCPaperView;
